"use client";

import { TopHeader } from "@/components/nav/top-header";
import { useEffect, useState } from "react";
import { formatCompact } from "@/lib/format";

interface Stats {
  person_entities: number;
  github_profiles: number;
  linkedin_profiles: number;
  repositories: number;
}

const formatNumber = (n: number) => formatCompact(n).toLowerCase();

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: "1 1 140px",
        padding: "1.25rem 1rem",
        background: "var(--surface-elevated)",
        border: "1px solid var(--border)",
        borderRadius: "10px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: "1.7rem",
          fontWeight: 700,
          color: "var(--text-primary)",
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "0.78rem",
          color: "var(--text-muted)",
          marginTop: "0.25rem",
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

export default function AboutPage() {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    fetch("/api/v1/stats")
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  return (
    <>
      <TopHeader />
      <main
        className="page-container"
        style={{ paddingTop: "8rem", paddingBottom: "2rem", maxWidth: "640px" }}
      >
        <section
          className="surface-card"
          style={{ padding: "2rem 1.75rem" }}
        >
          <h1 style={{ marginTop: 0, fontSize: "1.4rem", fontWeight: 700 }}>
            About Alister AI
          </h1>

          <p
            style={{
              color: "var(--text-secondary)",
              lineHeight: 1.65,
              fontSize: "0.92rem",
              marginBottom: "1.5rem",
            }}
          >
            Alister AI is a sourcing platform for the Czech tech market. We
            combine public engineering signals with AI-powered search to surface
            the strongest developers — not by keyword matching, but by
            understanding what great engineering work actually looks like.
          </p>

          <p
            style={{
              color: "var(--text-secondary)",
              lineHeight: 1.65,
              fontSize: "0.92rem",
              marginBottom: "1.75rem",
            }}
          >
            Every profile is Czech-based. Engineers are evaluated across their
            full professional footprint, so the best developers rise to the top
            regardless of how they describe themselves.
          </p>

          {stats && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <StatCard
                label="Developer Profiles"
                value={formatNumber(stats.person_entities)}
              />
              <StatCard
                label="GitHub Profiles"
                value={formatNumber(stats.github_profiles)}
              />
              <StatCard
                label="LinkedIn Profiles"
                value={formatNumber(stats.linkedin_profiles)}
              />
              <StatCard
                label="Repositories Indexed"
                value={formatNumber(stats.repositories)}
              />
            </div>
          )}
        </section>

        <section
          className="surface-card"
          style={{ padding: "2rem 1.75rem", marginTop: "1rem" }}
        >
          <h2 style={{ marginTop: 0, fontSize: "1.15rem", fontWeight: 700 }}>
            How It Works
          </h2>

          <ol
            style={{
              color: "var(--text-secondary)",
              lineHeight: 1.7,
              fontSize: "0.88rem",
              paddingLeft: "1.25rem",
              margin: "0",
              display: "flex",
              flexDirection: "column",
              gap: "0.6rem",
            }}
          >
            <li>
              <strong style={{ color: "var(--text-primary)" }}>
                Understand the brief
              </strong>{" "}
              — Every query is parsed for role, seniority, stack, domain, and
              constraints, so search works in plain language rather than
              boolean strings.
            </li>
            <li>
              <strong style={{ color: "var(--text-primary)" }}>
                One person, every source
              </strong>{" "}
              — We resolve identities across public sources, so a developer
              visible on multiple platforms appears as a single candidate with
              the full picture attached.
            </li>
            <li>
              <strong style={{ color: "var(--text-primary)" }}>
                Rank by real signal
              </strong>{" "}
              — Candidates are ordered by how well they match the brief and by
              quality signals from their professional footprint — not by how
              loudly they describe themselves.
            </li>
            <li>
              <strong style={{ color: "var(--text-primary)" }}>
                Work from where you already are
              </strong>{" "}
              — Alister connects to Claude.ai via MCP, so sourcing happens as
              a conversation: ask, refine, shortlist, and export without
              leaving the chat.
            </li>
          </ol>
        </section>
      </main>
    </>
  );
}
